import React, { ReactChild, useEffect, useState } from "react";
import styled from "styled-components";
import { layout, position, LayoutProps, PositionProps } from "styled-system";
import { Box } from "@bca/ui-library";

import { useWindowOffset } from "./useWindowOffset.hook";

import LeftArrowSvg from "./Icons/LeftArrowSVG";
import RightArrowSvg from "./Icons/RightArrowSVG";
import { isTouchDevice } from "../helpers/isTouchDevice";

interface ImageCarouselProps extends LayoutProps {
  children: any;
  activeImageIndex: number;
  slideSpacing: number;
  withTransition: boolean;
  hasImageFullWidth?: boolean;
  goToNextImage: () => void;
  goToPreviousImage: () => void;
  onTouchStart: (evt: any) => void;
  onTouchMove: (evt: any) => void;
  onTouchEnd: () => void;
}

interface SlidesContainerProps extends LayoutProps, PositionProps {}
interface ArrowProps {
  "data-testid": string;
  children?: React.ReactNode;
  onClick: () => void;
}

const SlidesContainer = styled(Box)<SlidesContainerProps>`
  ${layout}
  ${position}
`;

const ChildrenContainer = styled(Box)<PositionProps>`
  transition: ${(props) => (props["data-transition"] ? "left 0.5s" : "none")};
  ${position}
`;

const LeftArrow = styled.button<ArrowProps>`
  position: absolute;
  left: 24px;
  width: 25px;
  height: 40px;
  z-index: 2;
  border: none;
  padding: 0;
  background: transparent;
`;

const RightArrow = styled.button<ArrowProps>`
  position: absolute;
  left: calc(100vw - 55px);
  width: 25px;
  height: 40px;
  z-index: 2;
  border: none;
  padding: 0;
  background: transparent;
`;

const ImageCarousel: React.FunctionComponent<ImageCarouselProps> = ({
  children,
  goToNextImage,
  goToPreviousImage,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  activeImageIndex,
  slideSpacing,
  withTransition,
  hasImageFullWidth,
}) => {
  const [totalWidth, setTotalWidth] = useState(10000);
  const [slideWidth, setSlideWidth] = useState(0);
  const [totalHeight, setTotalHeight] = useState<any>([250, 400, 400]);
  const [computeAndSetOffset, windowOffsetRef] = useWindowOffset();

  const windowOffset = hasImageFullWidth ? 0 : windowOffsetRef.current;
  const offsetLeft = -(
    activeImageIndex * (slideWidth + slideSpacing) -
    windowOffset
  );
  const hasOneImage = children.length === 1;

  useEffect(() => {
    computeAndSetOffset();
  }, [computeAndSetOffset]);

  useEffect(() => {
    updateSlideSizes();
    window.addEventListener("resize", updateSlideSizes);
    return () => {
      window.removeEventListener("resize", updateSlideSizes);
    };
  }, []);

  const updateSlideSizes = () => {
    const height = getCarouselHeight(hasImageFullWidth);
    const width = getSlideWidth(hasImageFullWidth);
    let totalWidth = hasOneImage
      ? window.innerWidth
      : children.length * (width + slideSpacing);
    setSlideWidth(width);
    setTotalWidth(totalWidth);
    setTotalHeight(height);
  };

  return (
    <SlidesContainer
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      width={totalWidth}
      data-testid="image-carousel"
      height={totalHeight}
      position="relative"
      overflow="hidden"
      zIndex={0}
      display="flex"
      alignItems="center"
    >
      {!isTouchDevice && !hasOneImage && (
        <LeftArrow data-testid="left-arrow" onClick={goToPreviousImage}>
          <LeftArrowSvg width="100%" height="100%" />
        </LeftArrow>
      )}
      <ChildrenContainer
        data-transition={withTransition}
        left={offsetLeft}
        width="100%"
        display="flex"
        position="absolute"
      >
        {children}
      </ChildrenContainer>
      {!isTouchDevice && !hasOneImage && (
        <RightArrow data-testid="right-arrow" onClick={goToNextImage}>
          <RightArrowSvg width="100%" height="100%" />
        </RightArrow>
      )}
    </SlidesContainer>
  );
};

const getSlideWidth = (hasImageFullWidth) => {
  if (hasImageFullWidth) {
    return window.innerWidth;
  }

  if (window.innerWidth < 640) {
    return 340;
  }

  if (window.innerWidth < 1024) {
    return 600;
  }

  return 715;
};

const getCarouselHeight = (hasImageFullWidth) => {
  if (typeof window === "undefined") {
    return 0;
  }

  if (hasImageFullWidth) {
    return "100%";
  }

  return [250, 400, 400];
};

export default ImageCarousel;
